import { useReducer, Reducer, useEffect, useMemo } from 'react';
import { Location, useLocation } from 'react-router-dom';

import { BaseResponse, requestApi } from '~/services/api-new';
import currencyFormatter from '~/libs/currency-formatter';
import { gameLiveCasinoDataSelector } from '~/components/live-casino/ducks/selectors';
import { selectCurrency, selectLocale } from '~/components/account/ducks/index';
import {
	jackpotsSelector,
	gameJackpotDataSelector,
	gameDetailByGameCodeSelector
} from '~/components/casino-shared/ducks/selectors';
import { selectFranchiseId, selectGameInfoSlotsDataEnabled } from '~/components/brand/ducks/selectors';
import { useCasinoSharedSelector } from '~/components/casino-shared/ducks/types';

import { CombinedGameInfo, GameInfo } from './types';

export const ACTION_PREFIX = '@komigen/gameInfo';
export const CLEAN_INFO_STATE = `${ACTION_PREFIX}/CLEAN_INFO_STATE`;
export const SET_GAME_INFO = `${ACTION_PREFIX}/SET_GAME_INFO`;
export const SET_COLLAPSE_DATA = `${ACTION_PREFIX}/SET_COLLAPSE_DATA`;
export const SET_IS_INFO_LOADING = `${ACTION_PREFIX}/SET_IS_INFO_LOADING`;

export interface GameInfoState {
	gameInfo: GameInfo | null;
	isInfoLoading: boolean;
}

export type GameInfoAction =
	| { type: typeof CLEAN_INFO_STATE }
	| { type: typeof SET_GAME_INFO; payload: GameInfo }
	| { type: typeof SET_IS_INFO_LOADING; payload: boolean };

export const initialState: GameInfoState = {
	gameInfo: null,
	isInfoLoading: false
};

const reducer: Reducer<GameInfoState, GameInfoAction> = (state, action) => {
	switch (action.type) {
		case CLEAN_INFO_STATE:
			return {
				...initialState
			};
		case SET_GAME_INFO:
			return {
				...state,
				gameInfo: action.payload
			};
		case SET_IS_INFO_LOADING:
			return {
				...state,
				isInfoLoading: action.payload
			};
		default:
			return state;
	}
};

export const fetchGameInfo = async (
	localDispatch: React.Dispatch<GameInfoAction>,
	game: { gameId: number; categoryCode: string },
	franchiseId: number,
	secondarySectionDataEnabled: boolean
) => {
	localDispatch({ type: SET_IS_INFO_LOADING, payload: true });

	const searchParams = new URLSearchParams();
	searchParams.append('gameId', game.gameId.toString());
	searchParams.append('franchiseId', franchiseId.toString());

	const liveCasinoGame = ['CASINO_LIVE_CASINO', 'CASINO_ROULETTE', 'CASINO_BLACK_JACK'].includes(game.categoryCode);

	if (!secondarySectionDataEnabled && !liveCasinoGame) {
		return;
	}

	try {
		const response = await requestApi<BaseResponse<GameInfo>>({
			endpoint: `/rest/casino/gameCardInfo?${searchParams.toString()}`
		});

		if (response.status === 'SUCCESS' && response.result) {
			localDispatch({
				type: SET_GAME_INFO,
				payload: liveCasinoGame
					? {
							textKey: response.result.textKey
						}
					: response.result
			});
		} else {
			localDispatch({ type: SET_GAME_INFO, payload: {} });
		}
	} catch (error) {
		localDispatch({ type: SET_GAME_INFO, payload: {} });
	} finally {
		localDispatch({ type: SET_IS_INFO_LOADING, payload: false });
	}
};

function createSeats(totalSeats?: number, activeSeats?: number[]) {
	if (!totalSeats || !activeSeats) return [];

	return Array.from({ length: totalSeats }, (_, position) => ({
		position,
		active: activeSeats.includes(position)
	}));
}

// Custom hook
const useGameInfoReducer = ({ isLoadingTranslations }: { isLoadingTranslations: boolean }) => {
	const [gameInfoState, localDispatch] = useReducer(reducer, initialState);

	// Game data
	const location = useLocation() as Location<{ gameCode: string; listName: string; listGameIndex: string }>;
	const gameCode = location?.state?.gameCode;

	const game = useCasinoSharedSelector(state => gameDetailByGameCodeSelector(state, gameCode));

	// LC data
	const gameLiveCasinoData = useCasinoSharedSelector(state => gameLiveCasinoDataSelector(state, game));

	// Jackpot data
	// TODO: remove this logic to a custom hook like useJackpotData etc. since it's duplicating
	const jackpotData = useCasinoSharedSelector(state =>
		gameJackpotDataSelector(state, game ?? { provider: { code: '', name: '' } })
	);
	const currency = useCasinoSharedSelector(selectCurrency);
	const locale = useCasinoSharedSelector(selectLocale);
	const jackpots = useCasinoSharedSelector(jackpotsSelector);
	const { amount: realTimeJackpotAmount } = jackpots?.[game?.jackpotName || ''] || {};
	const secondarySectionDataEnabled = useCasinoSharedSelector(selectGameInfoSlotsDataEnabled);

	const preferredJackpotAmount = realTimeJackpotAmount || jackpotData || game?.jackpot;
	const formattedJackpotAmount = currencyFormatter.format(preferredJackpotAmount, currency, locale, true) as string;

	// Game info data
	const franchiseId = useCasinoSharedSelector(selectFranchiseId);

	useEffect(() => {
		if (game && !gameInfoState.isInfoLoading && !gameInfoState.gameInfo && !isLoadingTranslations) {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			fetchGameInfo(localDispatch, game, franchiseId, secondarySectionDataEnabled);
		}

		return () => {
			if (!gameCode) {
				localDispatch({ type: CLEAN_INFO_STATE });
			}
		};
	}, [
		localDispatch,
		game,
		gameInfoState.isInfoLoading,
		gameInfoState.gameInfo,
		franchiseId,
		gameCode,
		isLoadingTranslations,
		secondarySectionDataEnabled
	]);

	// Combining all data
	const gameInfo = useMemo(() => {
		let resultGameInfo: CombinedGameInfo = gameInfoState.gameInfo || {};

		// Add formatted jackpot amount if available
		if (formattedJackpotAmount) {
			resultGameInfo = { ...resultGameInfo, jackpot: formattedJackpotAmount };
		}

		// Add live casino data if available
		if (gameLiveCasinoData) {
			const { betLimits, type, status } = gameLiveCasinoData;
			resultGameInfo = {
				...resultGameInfo,
				betLimits,
				type,
				seats: createSeats(status?.totalSeats, status?.activeSeats),
				lastWinningNumbers: status?.rouletteNumbers,
				dealer: status?.dealer?.name,
				inGameImage: status?.inGameImage
			};
		}

		// Add game tags as features if available
		if (game?.tags?.length) {
			resultGameInfo = { ...resultGameInfo, features: game.tags };
		}

		return resultGameInfo;
	}, [gameInfoState.gameInfo, formattedJackpotAmount, gameLiveCasinoData, game?.tags]);

	return { gameInfo, game };
};

export default useGameInfoReducer;
